import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { useEffect, useState, type ReactElement } from 'react';
import { Alert, Button, Drawer, Select, Skeleton } from 'antd';
import { EditOutlined, MessageOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from '@govard/i18n-core/react';
import { EntityDetailLayout } from '../../../components/entity-detail/EntityDetailLayout.js';
import { EntityHeader } from '../../../components/entity-detail/EntityHeader.js';
import { EntityTabs } from '../../../components/entity-detail/EntityTabs.js';
import { EntityFieldList } from '../../../components/entity-detail/EntityFieldList.js';
import { EntityMetric } from '../../../components/entity-detail/EntityMetric.js';
import { EntityActivityHistory } from '../../../components/entity-detail/EntityActivityHistory.js';
import { EmptyState } from '../../../components/entity-detail/EmptyState.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { ErrorBoundary } from '../../../shared/ErrorBoundary.js';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { ProjectChatPanel } from '../../ai-chat/components/ChatPanel.js';
import { useMembers } from '../../tasks/hooks/useTaskLookups.js';
import { useTasks } from '../../tasks/hooks/useTasks.js';
import { useNotes } from '../../notes/hooks/useNotes.js';
import { useMeetings } from '../../meetings/hooks/useMeetings.js';
import {
  useDeleteProjectMutation,
  useSetProjectMembersMutation,
  useUpdateProjectMutation,
} from '../hooks/useProjectMutations.js';
import { useProject } from '../hooks/useProjects.js';
import { ProjectFormModal } from '../components/ProjectFormModal.js';
import { ProjectTasksTab } from '../components/ProjectTasksTab.js';
import { ProjectNotesTab } from '../components/ProjectNotesTab.js';
import { ProjectMeetingsTab } from '../components/ProjectMeetingsTab.js';
import { formatDate, getProjectStatusLabel, memberName, STATUS_TONE } from '../lib/projectMeta.js';

export function ProjectCardPage({ projectId }: { projectId: string }): ReactElement {
  const { t } = useTranslation();
  const scope = useCurrentScope();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [editOpen, setEditOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [memberDraft, setMemberDraft] = useState<{ id: string; values: string[] } | null>(null);
  const query = useProject(projectId);
  const update = useUpdateProjectMutation();
  const remove = useDeleteProjectMutation();
  const membersMutation = useSetProjectMembersMutation();
  const membersQuery = useMembers();
  const tasks = useTasks({ projectId });
  const meetings = useMeetings({ projectId });
  const notes = useNotes({ projectId, limit: 100 });
  const prefix = 'crm.features.features.projects.pages.projectcardpage.';
  useEffect(() => {
    setEditOpen(false);
    setChatOpen(false);
  }, [projectId]);
  useEffect(() => {
    if (!['details', 'copilot', 'chat', 'ai-copilot'].includes(params.get('tab') ?? '')) return;
    const next = new URLSearchParams(params);
    next.delete('tab');
    setParams(next, { replace: true });
  }, [params, setParams]);
  const openTab = (tab: string): void => {
    const next = new URLSearchParams(params);
    next.set('tab', tab);
    setParams(next);
  };
  if (query.isLoading)
    return (
      <main className="p-6">
        <Skeleton active />
      </main>
    );
  if (query.isError || !query.data)
    return (
      <main className="p-6">
        <ErrorState
          title={t(`${prefix}не_удалось_загрузить_проект`)}
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      </main>
    );
  const project = query.data;
  const savedMembers =
    memberDraft?.id === projectId
      ? memberDraft.values
      : project.members.map((entry) => entry.member.id);
  const membersDirty =
    savedMembers.length !== project.members.length ||
    savedMembers.some((id) => !project.members.some((entry) => entry.member.id === id));
  const mutationError = update.error ?? remove.error ?? membersMutation.error;
  const details = [
    { key: 'owner', label: t(`${prefix}владелец`), value: memberName(project.ownerMember) },
    {
      key: 'start',
      label: t(`${prefix}начало`),
      value: project.startDate ? formatDate(project.startDate) : undefined,
    },
    {
      key: 'due',
      label: t(`${prefix}срок`),
      value: project.dueDate ? formatDate(project.dueDate) : undefined,
    },
  ];
  const overview = (
    <div className="entity-overview">
      <section className="entity-section">
        <h2 className="entity-section-title">{t(`${prefix}описание`)}</h2>
        {project.description?.trim() ? (
          <p className="whitespace-pre-wrap break-words">{project.description}</p>
        ) : (
          <EmptyState text={t(`${prefix}описания_нет`)} />
        )}
      </section>
      <section className="entity-section">
        <h2 className="entity-section-title">{t(`${prefix}участники`)}</h2>
        <div className="entity-list-toolbar">
          <Select
            mode="multiple"
            className="min-w-0 flex-1"
            options={(membersQuery.data ?? []).map((member) => ({
              value: member.id,
              label: memberName(member),
            }))}
            value={savedMembers}
            onChange={(values) => setMemberDraft({ id: projectId, values })}
            loading={membersQuery.isLoading}
            aria-label={t(`${prefix}участники`)}
            placeholder={t(`${prefix}выберите_участников`)}
            data-testid="project-members-select"
          />
          <Button
            type="primary"
            loading={membersMutation.isPending}
            disabled={!membersDirty}
            onClick={() =>
              void membersMutation.mutateAsync({ id: project.id, memberIds: savedMembers }).then(
                () => setMemberDraft(null),
                () => undefined,
              )
            }
            data-testid="project-members-save"
          >
            {t(`${prefix}сохранить`)}
          </Button>
        </div>
        {project.members.length === 0 ? <EmptyState text={t(`${prefix}участников_нет`)} /> : null}
      </section>
    </div>
  );
  return (
    <ErrorBoundary>
      <EntityDetailLayout
        header={
          <>
            <EntityHeader
              backTo="/projects"
              backLabel={t(`${prefix}к_проектам`)}
              title={project.name}
              chips={[
                <EntityStatusChip key="status" tone={STATUS_TONE[project.status]}>
                  {getProjectStatusLabel(project.status)}
                </EntityStatusChip>,
              ]}
              primary={{
                key: 'chat',
                testId: 'project-open-copilot',
                icon: <MessageOutlined aria-hidden />,
                label: t('crm.entity_detail.open_chat'),
                onClick: () => setChatOpen(true),
              }}
              secondary={[
                {
                  key: 'edit',
                  testId: 'project-edit',
                  icon: <EditOutlined />,
                  label: t(`${prefix}редактировать`),
                  onClick: () => setEditOpen(true),
                },
              ]}
              overflow={[
                {
                  key: 'history',
                  label: t('crm.entity_detail.history'),
                  onClick: () => openTab('history'),
                },
                {
                  key: 'delete',
                  label: t('crm.shared.delete'),
                  danger: true,
                  disabled: remove.isPending,
                  confirm: {
                    title: t(`${prefix}удалить_проект`),
                    description: t(
                      `${prefix}задачи_встречи_и_заметки_останутся_но_отвяжутся_от_проекта`,
                    ),
                    okText: t('crm.shared.delete'),
                    cancelText: t('crm.common.cancel'),
                  },
                  onClick: () => remove.mutateAsync(project.id).then(() => navigate('/projects')),
                },
              ]}
            />
            {mutationError ? (
              <Alert type="error" showIcon closable title={mutationError.message} />
            ) : null}
          </>
        }
        sidebar={
          <>
            <section className="entity-panel">
              <h2 className="entity-section-title">{t('crm.entity_detail.summary')}</h2>
              <div className="grid grid-cols-2 gap-4">
                <EntityMetric label={t(`${prefix}задачи`)} value={project.taskCount} />
                <EntityMetric label={t(`${prefix}встречи`)} value={project.meetingCount} />
                <EntityMetric label={t(`${prefix}участники`)} value={project.members.length} />
                <EntityMetric
                  label={t(`${prefix}статус`)}
                  value={getProjectStatusLabel(project.status)}
                />
              </div>
            </section>
            <section className="entity-panel">
              <h2 className="entity-section-title">{t(`${prefix}детали`)}</h2>
              <EntityFieldList fields={details} />
            </section>
          </>
        }
        main={
          <EntityTabs
            items={[
              { key: 'overview', label: t('crm.entity_detail.overview'), content: overview },
              {
                key: 'tasks',
                label: t(`${prefix}задачи`),
                count: tasks.data?.length ?? project.taskCount,
                content: <ProjectTasksTab projectId={project.id} />,
              },
              {
                key: 'notes',
                label: t(`${prefix}заметки`),
                count: notes.data?.length ?? project.noteCount,
                content: <ProjectNotesTab projectId={project.id} />,
              },
              {
                key: 'meetings',
                label: t(`${prefix}встречи`),
                count: meetings.data?.length ?? project.meetingCount,
                content: <ProjectMeetingsTab projectId={project.id} />,
              },
              {
                key: 'history',
                label: t('crm.entity_detail.history'),
                content: <EntityActivityHistory entity="project" entityId={projectId} />,
              },
            ]}
          />
        }
      />
      <Drawer
        data-testid="project-copilot-drawer"
        title={t('crm.chat.drawer_title')}
        placement="right"
        size="min(760px, 100vw)"
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        styles={{ body: { padding: 0, overflow: 'hidden' } }}
      >
        {chatOpen ? <ProjectChatPanel scope={scope} projectId={project.id} /> : null}
      </Drawer>
      {editOpen ? (
        <ProjectFormModal open project={project} onClose={() => setEditOpen(false)} />
      ) : null}
    </ErrorBoundary>
  );
}
